/**
 * <ry-tabs>
 *
 * Tab interface with keyboard navigation.
 *
 * Minimal markup (recommended):
 * <ry-tabs>
 *   <ry-tab title="Tab 1" active>Content 1</ry-tab>
 *   <ry-tab title="Tab 2">Content 2</ry-tab>
 * </ry-tabs>
 *
 * JS uses data-ry-target for queries, CSS uses classes for styling.
 */

import { RyElement } from '../core/ry-element.js';

interface TabChangeDetail {
  index: number;
  trigger: HTMLElement;
  panel: HTMLElement;
}

export class RyTabs extends RyElement {
  setup(): void {
    // Transform minimal markup to full structure
    this.#transformMinimalMarkup();

    // Bind click handlers
    this.$$('[data-ry-target="trigger"]').forEach(trigger => {
      this.on(trigger, 'click', this.#handleTriggerClick);
    });

    // Keyboard navigation
    this.on(this, 'keydown', this.#handleKeydown);

    // Initialize ARIA
    this.#initAria();
  }

  #transformMinimalMarkup(): void {
    const tabs = this.$$('ry-tab');
    if (tabs.length === 0) return;

    const activeIndex = tabs.findIndex(t => t.hasAttribute('active'));
    const defaultActive = activeIndex === -1 ? 0 : activeIndex;

    // Build tab list
    // data-ry-target for JS queries, class for CSS styling
    const tabList = document.createElement('div');
    tabList.setAttribute('data-ry-target', 'list');
    tabList.className = 'ry-tabs__list';
    tabList.setAttribute('role', 'tablist');

    tabs.forEach((tab, index) => {
      const title = tab.getAttribute('title') ?? `Tab ${index + 1}`;
      const isActive = index === defaultActive;

      const trigger = document.createElement('button');
      trigger.setAttribute('data-ry-target', 'trigger');
      trigger.className = 'ry-tabs__trigger';
      trigger.setAttribute('role', 'tab');
      trigger.setAttribute('aria-selected', String(isActive));
      trigger.textContent = title;

      tabList.appendChild(trigger);

      // Convert ry-tab to panel
      tab.setAttribute('data-ry-target', 'panel');
      tab.className = 'ry-tabs__panel';
      tab.setAttribute('role', 'tabpanel');
      (tab as HTMLElement).dataset.ryState = isActive ? 'active' : '';
      tab.removeAttribute('title');
      tab.removeAttribute('active');
    });

    // Insert tab list at beginning
    this.insertBefore(tabList, this.firstChild);
  }

  #initAria(): void {
    const triggers = this.$$<HTMLElement>('[data-ry-target="trigger"]');
    const panels = this.$$<HTMLElement>('[data-ry-target="panel"], ry-tab');

    triggers.forEach((trigger, index) => {
      const panel = panels[index];
      if (!panel) return;

      // Generate IDs
      const triggerId = trigger.id || `ry-tab-${this.id || ''}-${index}`;
      const panelId = panel.id || `ry-tabpanel-${this.id || ''}-${index}`;

      trigger.id = triggerId;
      panel.id = panelId;

      trigger.setAttribute('aria-controls', panelId);
      panel.setAttribute('aria-labelledby', triggerId);

      // Set tabindex for keyboard nav
      trigger.setAttribute('tabindex', trigger.getAttribute('aria-selected') === 'true' ? '0' : '-1');
    });

    // Ensure one tab is active
    const activeIndex = triggers.findIndex(t => t.getAttribute('aria-selected') === 'true');
    if (activeIndex === -1 && triggers.length > 0) {
      this.select(0);
    }
  }

  #handleTriggerClick = (event: Event): void => {
    const trigger = event.currentTarget as HTMLElement;
    const triggers = this.$$<HTMLElement>('[data-ry-target="trigger"]');
    const index = triggers.indexOf(trigger);

    if (index !== -1) {
      this.select(index);
    }
  };

  #handleKeydown = (event: KeyboardEvent): void => {
    const triggers = this.$$<HTMLElement>('[data-ry-target="trigger"]');
    const currentIndex = triggers.indexOf(document.activeElement as HTMLElement);

    if (currentIndex === -1) return;

    let newIndex: number;

    switch (event.key) {
      case 'ArrowRight':
        event.preventDefault();
        newIndex = (currentIndex + 1) % triggers.length;
        this.select(newIndex);
        triggers[newIndex]?.focus();
        break;
      case 'ArrowLeft':
        event.preventDefault();
        newIndex = (currentIndex - 1 + triggers.length) % triggers.length;
        this.select(newIndex);
        triggers[newIndex]?.focus();
        break;
      case 'Home':
        event.preventDefault();
        this.select(0);
        triggers[0]?.focus();
        break;
      case 'End':
        event.preventDefault();
        this.select(triggers.length - 1);
        triggers[triggers.length - 1]?.focus();
        break;
    }
  };

  select(index: number): void {
    const triggers = this.$$<HTMLElement>('[data-ry-target="trigger"]');
    const panels = this.$$<HTMLElement>('[data-ry-target="panel"], ry-tab');

    if (index < 0 || index >= triggers.length) return;

    // Deactivate all
    triggers.forEach(trigger => {
      trigger.setAttribute('aria-selected', 'false');
      trigger.setAttribute('tabindex', '-1');
    });

    panels.forEach(panel => {
      panel.dataset.ryState = '';
      panel.removeAttribute('active');
    });

    // Activate selected
    const trigger = triggers[index];
    const panel = panels[index];

    if (trigger && panel) {
      trigger.setAttribute('aria-selected', 'true');
      trigger.setAttribute('tabindex', '0');
      panel.dataset.ryState = 'active';

      this.emit<TabChangeDetail>('change', { index, trigger, panel });
    }
  }

  get selectedIndex(): number {
    const triggers = this.$$('[data-ry-target="trigger"]');
    return triggers.findIndex(t => t.getAttribute('aria-selected') === 'true');
  }
}

customElements.define('ry-tabs', RyTabs);

// Register placeholder for minimal markup
if (!customElements.get('ry-tab')) {
  customElements.define('ry-tab', class extends HTMLElement {});
}
